import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Instagram, Linkedin } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../lib/api';
import { useLanguage } from '../../lib/i18n';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_creative-ai-studio-151/artifacts/0wsobm4i_zsdigitals%20logo.jpeg';

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.83a8.16 8.16 0 0 0 4.77 1.52V6.89a4.85 4.85 0 0 1-1.84-.2Z"/></svg>
);

export default function Footer() {
  const [email, setEmail] = useState('');
  const [subStatus, setSubStatus] = useState('');
  const { t } = useLanguage();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try { await api.subscribeNewsletter({ email }); setSubStatus('success'); setEmail(''); setTimeout(() => setSubStatus(''), 3000); } catch { setSubStatus('error'); }
  };

  const footerLinks = {
    [t('footer.company')]: [
      { label: t('nav.about'), path: '/about' },
      { label: t('nav.careers'), path: '/careers' },
      { label: t('nav.contact'), path: '/contact' },
    ],
    [t('footer.servicesLabel')]: [
      { label: t('services.svc1.title'), path: '/services' },
      { label: t('services.svc2.title'), path: '/services' },
      { label: t('services.svc3.title'), path: '/services' },
    ],
    [t('footer.resources')]: [
      { label: t('nav.caseStudies'), path: '/case-studies' },
      { label: t('nav.portfolio'), path: '/portfolio' },
    ],
  };

  return (
    <footer className="bg-[#050508] text-white border-t border-[#7C3AED]/10" data-testid="site-footer">
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-12 sm:py-16 md:py-24">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 sm:gap-8">
            <div>
              <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="font-poppins font-bold text-2xl sm:text-3xl md:text-4xl text-white mb-2 sm:mb-3">{t('footer.stayUpdated')}</motion.h2>
              <p className="text-[#A1A1AA] text-sm sm:text-base max-w-md">{t('footer.stayUpdatedSub')}</p>
            </div>
            <form onSubmit={handleSubscribe} className="flex gap-2 sm:gap-3 w-full md:w-auto" data-testid="newsletter-form">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('footer.emailPlaceholder')} className="flex-1 md:w-64 lg:w-72 px-4 sm:px-5 py-2.5 sm:py-3 rounded-full bg-white/5 border border-[#7C3AED]/20 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#7C3AED] transition-colors" data-testid="newsletter-email-input" />
              <button type="submit" className="px-4 sm:px-6 py-2.5 sm:py-3 bg-[#7C3AED] text-white font-semibold rounded-full hover:shadow-[0_0_25px_rgba(124,58,237,0.4)] transition-all duration-300 flex items-center gap-2 whitespace-nowrap text-sm" data-testid="newsletter-submit-button">
                {t('footer.subscribe')} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
            {subStatus === 'success' && <p className="text-[#7C3AED] text-sm">Subscribed!</p>}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-10 sm:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12">
          <div>
            <Link to="/" className="flex items-center gap-2 mb-4 sm:mb-6">
              <img src={LOGO_URL} alt="ZS Digitals" className="h-8 w-8 rounded-lg object-contain" />
              <span className="font-poppins font-bold text-base sm:text-lg">ZS Digitals</span>
            </Link>
            <p className="text-white/40 text-xs sm:text-sm leading-relaxed mb-4 sm:mb-6">{t('footer.tagline')}</p>
            <div className="flex gap-3">
              {[
                { Icon: Instagram, name: 'instagram', href: 'https://www.instagram.com/zsdigitals.ai?igsh=Z2o5MHQyaGkxeGZo&utm_source=qr' },
                { Icon: TikTokIcon, name: 'tiktok', href: 'https://www.tiktok.com/@zsdigitals.ai?_r=1&_t=ZS-96UZPw7Yy9N' },
                { Icon: Linkedin, name: 'linkedin', href: 'https://www.linkedin.com/company/zsdigitals2022/about/?viewAsMember=true' },
              ].map(({ Icon, name, href }) => (
                <a key={name} href={href} target="_blank" rel="noopener noreferrer" aria-label={name} className="w-8 sm:w-9 h-8 sm:h-9 rounded-full bg-white/5 flex items-center justify-center hover:bg-[#7C3AED] transition-all duration-300" data-testid={`footer-${name}-link`}><Icon className="w-3.5 sm:w-4 h-3.5 sm:h-4" /></a>
              ))}
            </div>
          </div>
          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title}>
              <h4 className="font-poppins font-semibold text-xs sm:text-sm uppercase tracking-wider text-white/60 mb-3 sm:mb-5">{title}</h4>
              <ul className="space-y-2 sm:space-y-3">
                {links.map((link) => (
                  <li key={link.label}><Link to={link.path} className="text-white/40 text-xs sm:text-sm hover:text-[#7C3AED] transition-colors duration-200">{link.label}</Link></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-4 sm:py-6 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
          <p className="text-white/30 text-xs sm:text-sm">&copy; {new Date().getFullYear()} ZS Digitals. {t('footer.rights')}</p>
          <div className="flex gap-4 sm:gap-6">
            <a href="#" className="text-white/30 text-xs sm:text-sm hover:text-white/60 transition-colors">{t('footer.privacy')}</a>
            <a href="#" className="text-white/30 text-xs sm:text-sm hover:text-white/60 transition-colors">{t('footer.terms')}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
